import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Key, Check, X, Phone } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PasswordRequests({ onBack }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Approve Modal state
  const [selectedReq, setSelectedReq] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/password-requests`);
      setRequests(response.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openApproveModal = (req) => {
    setSelectedReq(req);
    setNewPassword('123456');
  };

  const submitApprove = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      setSubmittingAction(true);
      await axios.post(`${API}/admin/password-requests/${selectedReq.id}/action`, {
        action: 'approve',
        new_password: newPassword
      });
      toast.success(`Password for ${selectedReq.username || 'Handler'} updated successfully!`);
      setSelectedReq(null);
      setNewPassword('');
      fetchRequests();
    } catch (err) {
      toast.error('Failed to approve request');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this password reset request?')) return;
    try {
      await axios.post(`${API}/admin/password-requests/${id}/action`, { action: 'reject' });
      toast.success('Request rejected');
      fetchRequests();
    } catch (err) {
      toast.error('Failed to reject request');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
      {/* Header Bar - Green matching reference screenshot */}
      <div className="bg-[#23d366] text-white px-4 py-3 shadow-md flex items-center gap-3 flex-shrink-0">
        {onBack && (
          <button onClick={onBack} className="p-1 hover:bg-black/10 rounded-full transition-colors cursor-pointer">
            <ArrowLeft size={22} weight="bold" />
          </button>
        )}
        <div>
          <h1 className="text-base font-bold leading-tight">Password Reset Requests</h1>
          <p className="text-[11px] text-white/90">Approve or reject password reset requests from handlers</p>
        </div>
      </div>

      {/* Main Container */}
      <div className="flex-1 p-4 md:p-8 max-w-2xl w-full mx-auto space-y-3">
        <h2 className="text-sm font-bold text-slate-800 mb-2">Pending Password Reset Requests</h2>

        {loading ? (
          <div className="p-12 text-center text-slate-500 bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2" />
            <p className="text-xs font-semibold">Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-slate-400 bg-white rounded-xl border border-slate-200 shadow-sm">
            <Key size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm font-medium">No pending password reset requests</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {requests.map((req) => (
              <div
                key={req.id}
                className="bg-white rounded-xl p-3.5 border border-slate-200 shadow-sm flex items-center justify-between gap-4 hover:border-slate-300 transition-colors"
                data-testid={`password-request-${req.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="font-bold text-xs text-slate-800 uppercase tracking-tight">
                      {req.username || req.name || 'User'}
                    </h3>
                    <span className="text-[11px] text-slate-500 font-normal">
                      ({req.role || 'user'})
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-slate-400 mt-0.5">
                    <Phone size={12} className="text-rose-400" weight="fill" />
                    <span>{req.phone || req.mobile || 'N/A'} - {formatDate(req.created_at)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openApproveModal(req)}
                    className="bg-[#00a884] hover:bg-[#008f72] text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-colors flex items-center gap-1 cursor-pointer"
                    data-testid={`approve-btn-${req.id}`}
                  >
                    <Key size={14} weight="bold" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(req.id)}
                    className="border border-red-500 text-red-500 hover:bg-red-50 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                    data-testid={`reject-btn-${req.id}`}
                  >
                    <X size={14} weight="bold" />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* APPROVE MODAL */}
      {selectedReq && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl space-y-4 font-sans border border-slate-200">
            <h3 className="text-base font-bold text-slate-900">
              Set New Password for {selectedReq.username || 'Handler'}
            </h3>
            <p className="text-xs text-slate-500">
              Enter a new password for handler ({selectedReq.phone || 'N/A'}).
            </p>
            <form onSubmit={submitApprove} className="space-y-4 pt-1">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">New Password</label>
                <input
                  type="text"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm font-mono font-bold text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Min 6 characters"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setSelectedReq(null)}
                  className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingAction}
                  className="px-4 py-2 bg-[#00a884] hover:bg-[#008f72] text-white rounded-xl text-xs font-bold shadow-sm transition-colors cursor-pointer disabled:opacity-50"
                >
                  {submittingAction ? 'Updating...' : 'Approve & Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
